import { useEffect, useRef, type MouseEvent } from 'react';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { Trash2 } from 'lucide-react';
import { useExitTransition } from '@/hooks/useExitTransition';
import { SHEET_EXIT_MS, scrimAnimation, sheetAnimation, sheetClassName } from '@/utils/sheetMotion';

interface DeleteAllSheetProps {
  open: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Confirmation for "Delete all": an iOS-style action sheet on mobile, a centered dialog
 * from lg up. One set of buttons restyled per breakpoint, so each action exists once.
 */
export function DeleteAllSheet({ open, onConfirm, onClose }: DeleteAllSheetProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const { mounted, closing } = useExitTransition(open, SHEET_EXIT_MS);

  useEffect(() => {
    if (!open) return;
    // Move focus into the dialog without ringing a button: Tab then reaches the actions.
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!mounted) return null;

  const handleScrimClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <Flex
      position="fixed"
      inset="0"
      zIndex="modal"
      bg="rgba(0, 0, 0, 0.35)"
      align={{ base: 'flex-end', lg: 'center' }}
      justify="center"
      px={{ base: 2, lg: 5 }}
      pt={{ base: 2, lg: 5 }}
      pb={{ base: 'max(8px, env(safe-area-inset-bottom, 0px))', lg: 5 }}
      animation={scrimAnimation(closing)}
      pointerEvents={closing ? 'none' : 'auto'}
      onClick={handleScrimClick}
    >
      <Box
        ref={dialogRef}
        tabIndex={-1}
        outline="none"
        role="dialog"
        aria-modal="true"
        aria-hidden={closing || undefined}
        aria-labelledby="delete-all-title"
        aria-describedby="delete-all-description"
        w="100%"
        maxW={{ base: '100%', lg: '420px' }}
        bg={{ base: 'transparent', lg: 'white' }}
        borderRadius={{ lg: '20px' }}
        p={{ base: 0, lg: 7 }}
        boxShadow={{ lg: '0 12px 32px rgba(31, 41, 50, 0.18)' }}
        // Mobile: the action sheet slides up. Desktop: the dialog scales in where it sits.
        className={sheetClassName(closing)}
        animation={{
          base: sheetAnimation(closing),
          lg: closing ? `fadeOut ${SHEET_EXIT_MS}ms ease-in forwards` : 'scaleIn 200ms cubic-bezier(0.2, 0, 0, 1)',
        }}
      >
        <Box
          bg="white"
          borderTopRadius={{ base: '14px', lg: 0 }}
          borderBottom={{ base: '1px solid', lg: 'none' }}
          borderColor="volyx.borderLight"
          px={{ base: 4, lg: 0 }}
          py={{ base: 3.5, lg: 0 }}
          textAlign={{ base: 'center', lg: 'left' }}
        >
          <Text
            id="delete-all-title"
            m="0"
            fontSize={{ base: '13px', lg: '20px' }}
            fontWeight={{ base: 'semibold', lg: 'bold' }}
            color={{ base: 'volyx.textSecondary', lg: 'volyx.text' }}
          >
            Delete all notifications?
          </Text>
          <Text
            id="delete-all-description"
            m="0"
            mt={{ base: 0.5, lg: 2 }}
            fontSize={{ base: '13px', lg: '14px' }}
            color={{ base: 'volyx.textSecondary', lg: 'volyx.textMuted' }}
          >
            This can’t be undone.
          </Text>
        </Box>
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          justify={{ lg: 'flex-end' }}
          gap={{ base: 2, lg: 3 }}
          mt={{ base: 0, lg: 6 }}
        >
          <chakra.button
            type="button"
            order={{ base: 1, lg: 2 }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="2"
            h={{ base: '56px', lg: 'auto' }}
            px={{ lg: '18px' }}
            py={{ lg: '10px' }}
            border="none"
            borderRadius={{ base: '0 0 14px 14px', lg: '10px' }}
            bg={{ base: 'white', lg: 'volyx.error' }}
            color={{ base: 'volyx.error', lg: 'white' }}
            fontSize={{ base: '17px', lg: '14px' }}
            fontWeight="semibold"
            cursor="pointer"
            onClick={onConfirm}
          >
            <Box as="span" display={{ base: 'inline-flex', lg: 'none' }}>
              <Trash2 size={18} aria-hidden />
            </Box>
            Delete all
          </chakra.button>
          <chakra.button
            type="button"
            order={{ base: 2, lg: 1 }}
            h={{ base: '56px', lg: 'auto' }}
            px={{ lg: '18px' }}
            py={{ lg: '10px' }}
            border={{ base: 'none', lg: '1px solid' }}
            borderColor="volyx.borderLight"
            borderRadius={{ base: '14px', lg: '10px' }}
            bg="white"
            color={{ base: 'volyx.action', lg: 'volyx.text' }}
            fontSize={{ base: '17px', lg: '14px' }}
            fontWeight="semibold"
            cursor="pointer"
            onClick={onClose}
          >
            Cancel
          </chakra.button>
        </Flex>
      </Box>
    </Flex>
  );
}
